import React, { useState, useRef } from "react";
import "./App.scss";
import MomentUtils from "@date-io/moment";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { Provider } from "react-redux";
import configStore from "./redux/store";
import AppContext from "./AppContext";
import api from "./api";
import Initializer from "./Initializer";
import { hasElements } from "utils/arrays";
import NotificationsMonitor from "NotificationsMonitor";

import { Buffer } from "buffer";
window.Buffer = window.Buffer || Buffer;

export default function App() {
    const [allowedModules, setAllowedModules] = useState(undefined);
    const [configurations, setConfigurations] = useState([]);
    const [module, setModule] = useState(undefined);
    const [messaging, setMessaging] = useState({
        profile: undefined, // The identity for conversations
        messages: [],
        selectedConversation: undefined,
        twilioConversations: {
            client: undefined, // The client object
            clientState: "disconnected",
            initialized: undefined, // True or false
            connectionState: "disconnected",
        },
    });
    const [conversations, setConversations] = useState([]);
    const notifications = useRef({});
    const observers = useRef([]);

    const setNotifications = (payload) => {
        notifications.current = payload;
        observers?.current?.forEach(({ caller, callback }) => {
            typeof callback === "function" && callback(payload);
        });
    };

    const subscribeToNotifications = (caller, callback) => {
        if (!observers.current.some((x) => x.caller === caller)) {
            observers.current.push({ caller, callback });
            typeof callback === "function" && callback(notifications.current);
        }

        const subscription = {
            caller,
            unsubscribe: () => {
                observers.current = observers.current.filter((x) => x.caller !== caller);
            },
        };
        return subscription;
    };

    const changeChatProfile = (profile) => {
        setMessaging((prev) => ({
            ...prev,
            profile,
            messages: [],
            selectedConversation: undefined,
        }));
        setConversations([]);
    };

    const setMessages = (fnOrValue) => {
        if (fnOrValue) {
            if (typeof fnOrValue === "function") {
                setMessaging((prev) => ({ ...prev, messages: fnOrValue(prev.messages) }));
            } else {
                setMessaging((prev) => ({ ...prev, messages: fnOrValue }));
            }
        }
    };

    const setSelectedConversation = (newSelectedConversation) => {
        setMessaging((prev) => {
            if (prev.selectedConversation) {
                prev.selectedConversation.removeAllListeners("messageAdded");
            }
            newSelectedConversation.removeAllListeners("messageAdded");
            newSelectedConversation.on("messageAdded", function (message) {
                setMessages((prev) => {
                    if (hasElements(prev) && prev.some((m) => m.sid === message.sid)) {
                        return prev;
                    }
                    return [...prev, message];
                });
            });
            return { ...prev, selectedConversation: newSelectedConversation };
        });
    };

    const markCachedNotificationsAsRead = (identity, ids) => {
        setNotifications((prev) => ({ ...prev, [identity]: prev[identity].filter((n) => !ids.includes(n.id)) }));
    };

    const joinConversation = (newConversation) => {
        setConversations((prev) => {
            if (hasElements(prev) && prev.some((c) => c.sid === newConversation.sid)) {
                return prev;
            }
            return [...prev, newConversation];
        });
    };

    const leaveConversation = (conversation) => {
        setConversations((prev) => [...prev.filter((conv) => conv !== conversation)]);
    };

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <AppContext.Provider
                value={{
                    api,
                    configurations,
                    setConfigurations,
                    allowedModules,
                    setAllowedModules,
                    module,
                    setModule,
                    messaging,
                    setMessaging,
                    joinConversation,
                    leaveConversation,
                    changeChatProfile,
                    setSelectedConversation,
                    setMessages,
                    subscribeToNotifications,
                    setNotifications,
                    markCachedNotificationsAsRead,
                    conversations,
                    setConversations,
                }}
            >
                <Provider store={configStore.store}>
                    <Initializer />
                    <NotificationsMonitor />
                </Provider>
            </AppContext.Provider>
        </MuiPickersUtilsProvider>
    );
}
